import { curveMonotoneX } from '@visx/curve';
import { localPoint } from '@visx/event';
import { Group } from '@visx/group';
import { scaleLinear, scaleTime } from '@visx/scale';
import { AreaClosed, LinePath } from '@visx/shape';
import { useId, useMemo, useState } from 'react';
import type { KeyboardEvent, MouseEvent } from 'react';
import { formatAbsolute, formatCompactNumber } from '../lib/format.ts';
import { ChartFrame, ChartTooltip } from './ChartFrame.tsx';
import type { ChartBaseProps } from './ChartFrame.tsx';
import { PLOT, ThresholdLine, XAxis, YAxis, timeTickFormat } from './axes.tsx';
import { MAX_SERIES, colourFor } from './palette.ts';

export interface SeriesPoint {
  /** A timestamp in ms. */
  x: number;
  /** Missing samples are null: the line breaks there. */
  y: number | null;
}

export interface Series {
  key: string;
  label: string;
  points: SeriesPoint[];
}

export interface LineChartProps extends ChartBaseProps {
  /** At most 6; fold the rest into "Other" before passing them. */
  series: Series[];
  /** Fill under the line: only for one series (data-viz.md). */
  area?: boolean;
  /** Formats values on the axis and in the tooltip (default compact: 12.3K). */
  format?: (n: number) => string;
  /** Formats the axis ticks (default `format`); money axes read best whole: formatMoneyCompact. */
  axisFormat?: (n: number) => string;
  /** Formats the x value in the tooltip (default the full date and time). */
  xFormat?: (x: number) => string;
  /** A budget or limit: a dashed line with a label. */
  threshold?: { value: number; label: string };
  /** Every series key the page knows, in slot order, so a series keeps its colour across charts. */
  order?: string[];
  /** Start the y axis at zero (default true). */
  zero?: boolean;
}

/**
 * How one or a few values changed over time (data-viz.md): 1.5 px lines, points only on
 * hover, horizontal grid, at most 5 ticks. Lines are labelled at their end when there are 4 or
 * fewer; otherwise a legend sits above. Hover or focus shows a crosshair and every series'
 * value; arrow keys move it.
 */
export function LineChart({ series, area, format = formatCompactNumber, axisFormat, xFormat = formatAbsolute, threshold, order, zero = true, height = 200, ...base }: LineChartProps) {
  const clip = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const [hover, setHover] = useState<number | null>(null);
  const shown = series.slice(0, MAX_SERIES);
  const keys = order ?? shown.map((s) => s.key);
  const directLabels = shown.length > 1 && shown.length <= 4;
  const xs = useMemo(() => [...new Set(shown.flatMap((s) => s.points.map((p) => p.x)))].sort((a, b) => a - b), [shown]);
  const values = shown.flatMap((s) => s.points.map((p) => p.y)).filter((v): v is number => v != null);
  const isEmpty = xs.length === 0 || values.length === 0;

  const legend = shown.map((s) => ({ key: s.key, label: s.label, colour: colourFor(s.key, keys) }));
  const table = {
    columns: ['Time', ...shown.map((s) => s.label)],
    rows: xs.map((x) => [xFormat(x), ...shown.map((s) => fmtOrBlank(s.points.find((p) => p.x === x)?.y, format))]),
  };

  return (
    <ChartFrame {...base} height={height} isEmpty={isEmpty} legend={directLabels ? undefined : legend} table={table}>
      {(width) => {
        const plot = { ...PLOT, tail: directLabels ? 96 : PLOT.tail };
        const iw = Math.max(0, width - plot.yAxisBand - plot.tail);
        const ih = Math.max(0, height - plot.headroom - plot.xAxisBand);
        const domain: [number, number] = [xs[0] ?? 0, xs[xs.length - 1] ?? 1];
        const x = scaleTime<number>({ domain, range: [0, iw] });
        const top = Math.max(...values, threshold?.value ?? -Infinity);
        const bottom = zero ? Math.min(0, ...values) : Math.min(...values);
        const y = scaleLinear<number>({ domain: [bottom, top === bottom ? top + 1 : top], range: [ih, 0], nice: true });
        const atX = (s: Series, xv: number) => s.points.find((p) => p.x === xv)?.y ?? null;

        const pick = (e: MouseEvent<SVGRectElement>) => {
          const p = localPoint(e);
          if (!p) return;
          const target = x.invert(p.x - plot.yAxisBand).getTime();
          let best = 0;
          for (let i = 1; i < xs.length; i++) if (Math.abs((xs[i] ?? 0) - target) < Math.abs((xs[best] ?? 0) - target)) best = i;
          setHover(best);
        };
        const onKey = (e: KeyboardEvent<SVGSVGElement>) => {
          if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight' && e.key !== 'Home' && e.key !== 'End') return;
          e.preventDefault();
          const last = xs.length - 1;
          setHover((h) => {
            const cur = h ?? last;
            if (e.key === 'Home') return 0;
            if (e.key === 'End') return last;
            return Math.min(last, Math.max(0, cur + (e.key === 'ArrowRight' ? 1 : -1)));
          });
        };
        const hx = hover == null ? null : xs[hover];

        return (
          <>
            <svg
              width={width}
              height={height}
              tabIndex={0}
              aria-label={`${base.summary}. Use the arrow keys to read each point.`}
              onKeyDown={onKey}
              onFocus={() => setHover(xs.length - 1)}
              onBlur={() => setHover(null)}
              className="overflow-visible outline-offset-2"
            >
              <defs>
                <clipPath id={clip}>
                  <rect x={0} y={-2} width={iw} height={ih + 4} />
                </clipPath>
              </defs>
              <Group left={plot.yAxisBand} top={plot.headroom}>
                <YAxis scale={y} width={iw} format={axisFormat ?? format} />
                <XAxis scale={x} top={ih} format={(v) => timeTickFormat(domain)(Number(v))} />
                {threshold && <ThresholdLine y={y(threshold.value)} width={iw} label={threshold.label} />}
                <g clipPath={`url(#${clip})`}>
                  {shown.map((s) => {
                    const colour = colourFor(s.key, keys);
                    return (
                      <g key={s.key}>
                        {area && shown.length === 1 && (
                          <AreaClosed<SeriesPoint>
                            data={s.points}
                            x={(p) => x(p.x)}
                            y={(p) => y(p.y ?? 0)}
                            yScale={y}
                            defined={(p) => p.y != null}
                            curve={curveMonotoneX}
                            fill={colour}
                            fillOpacity={0.12}
                          />
                        )}
                        <LinePath<SeriesPoint>
                          data={s.points}
                          x={(p) => x(p.x)}
                          y={(p) => y(p.y ?? 0)}
                          defined={(p) => p.y != null}
                          curve={curveMonotoneX}
                          stroke={colour}
                          strokeWidth={1.5}
                          strokeLinejoin="round"
                          strokeLinecap="round"
                          data-series={s.key}
                        />
                      </g>
                    );
                  })}
                </g>
                {directLabels &&
                  shown.map((s) => {
                    const last = [...s.points].reverse().find((p) => p.y != null);
                    if (!last || last.y == null) return null;
                    return (
                      <text key={s.key} x={iw + 6} y={y(last.y) + 4} fill="var(--fg-muted)" fontSize={12} fontFamily="var(--font-sans)">
                        <tspan fill={colourFor(s.key, keys)}>{'● '}</tspan>
                        {s.label}
                      </text>
                    );
                  })}
                {hx != null && (
                  <g pointerEvents="none">
                    <line x1={x(hx)} x2={x(hx)} y1={0} y2={ih} stroke="var(--border-strong)" strokeWidth={1} />
                    {shown.map((s) => {
                      const v = atX(s, hx);
                      return v == null ? null : <circle key={s.key} cx={x(hx)} cy={y(v)} r={4} fill={colourFor(s.key, keys)} stroke="var(--surface)" strokeWidth={2} />;
                    })}
                  </g>
                )}
                <rect width={iw} height={ih} fill="transparent" onMouseMove={pick} onMouseLeave={() => setHover(null)} />
              </Group>
            </svg>
            {hx != null && (
              <ChartTooltip
                width={width}
                left={plot.yAxisBand + x(hx)}
                top={plot.headroom + ih / 2}
                heading={xFormat(hx)}
                rows={shown.map((s) => ({ key: s.key, label: s.label, colour: colourFor(s.key, keys), value: fmtOrBlank(atX(s, hx), format) }))}
              />
            )}
          </>
        );
      }}
    </ChartFrame>
  );
}

/** One series filled under its line. */
export function AreaChart(props: Omit<LineChartProps, 'area'>) {
  return <LineChart {...props} area />;
}

function fmtOrBlank(v: number | null | undefined, format: (n: number) => string): string {
  return v == null ? '' : format(v);
}
